import React, { useState, useRef } from 'react';
import { HiOutlineX, HiOutlineCamera, HiOutlineUpload, HiOutlineDocumentText, HiOutlineTrash } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import './ProofUploadModal.css';

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB, matches backend deliveryProofConfig
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

const ProofUploadModal = ({ order, onClose, onSuccess }) => {
    // Each entry: { file, previewUrl (images only) }
    const [files, setFiles] = useState([]);
    const [note, setNote] = useState('');
    const [paymentType, setPaymentType] = useState(order.paymentType || 'COD');
    const [amountPaid, setAmountPaid] = useState(order.amountPaid || '');
    const [paymentNotes, setPaymentNotes] = useState(order.paymentNotes || '');
    // The common case in one trip: goods handed over, invoice not raised yet.
    // Pre-checked from the order so re-uploading proof doesn't silently clear
    // a flag the driver already raised.
    const [invoicePending, setInvoicePending] = useState(Boolean(order.invoicePending));
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleFilesChange = (e) => {
        const selected = Array.from(e.target.files || []);
        const accepted = [];

        for (const file of selected) {
            if (file.size > MAX_FILE_SIZE) {
                alert(`"${file.name}" is larger than 50MB and was skipped`);
                continue;
            }
            if (!ALLOWED_TYPES.includes(file.type)) {
                alert(`"${file.name}" must be JPG, PNG, or PDF — skipped`);
                continue;
            }
            accepted.push({
                file,
                previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
            });
        }

        setFiles(prev => [...prev, ...accepted]);
        e.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setFiles(prev => {
            const removed = prev[index];
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (files.length === 0) {
            alert('Please select at least one photo or PDF');
            return;
        }

        // Note is optional - delivery boy can leave it empty

        // Validate amount paid
        if (amountPaid !== '' && amountPaid !== null) {
            const amountPaidNum = parseFloat(amountPaid);
            if (isNaN(amountPaidNum) || amountPaidNum < 0) {
                alert('Amount paid must be a valid positive number');
                return;
            }
            if (amountPaidNum > order.amount) {
                alert(`Amount paid (${amountPaidNum}) cannot exceed total amount (${order.amount})`);
                return;
            }
        }

        try {
            setUploading(true);

            const formData = new FormData();
            files.forEach(({ file }) => formData.append('files', file));
            formData.append('proofNote', note);
            formData.append('paymentType', paymentType);
            if (amountPaid !== '' && amountPaid !== null) {
                formData.append('amountPaid', amountPaid);
            }
            if (paymentNotes.trim()) {
                formData.append('paymentNotes', paymentNotes);
            }
            formData.append('invoicePending', invoicePending ? 'true' : 'false');

            await axiosInstance.post(`/api/deliveries/${order._id}/proof`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            alert('Proof uploaded successfully!');
            onSuccess();
        } catch (error) {
            console.error('Error uploading proof:', error);
            alert(error.response?.data?.message || 'Failed to upload proof');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="proof-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Upload Delivery Proof</h2>
                    <button className="close-btn" onClick={onClose}>
                        <HiOutlineX />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="order-summary">
                        <h3>Order Details</h3>
                        <p><strong>Invoice:</strong> {order.invoiceNo}</p>
                        <p><strong>Customer:</strong> {order.customerName}</p>
                        <p><strong>Area:</strong> {order.area}</p>
                        <p><strong>Total Amount:</strong> QAR {order.amount?.toLocaleString()}</p>
                        <p><strong>Already Paid:</strong> QAR {(order.amountPaid || 0).toLocaleString()}</p>
                        <p><strong>Balance:</strong> QAR {((order.amount || 0) - (order.amountPaid || 0)).toLocaleString()}</p>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Delivery Proof *</label>
                            <div className="photo-upload-area">
                                {files.length > 0 && (
                                    <div className="proof-file-list">
                                        {files.map((entry, index) => (
                                            <div className="proof-file-item" key={index}>
                                                {entry.previewUrl ? (
                                                    <img src={entry.previewUrl} alt={entry.file.name} />
                                                ) : (
                                                    <div className="proof-file-icon">
                                                        <HiOutlineDocumentText size={28} />
                                                    </div>
                                                )}
                                                <span className="proof-file-name" title={entry.file.name}>{entry.file.name}</span>
                                                <button
                                                    type="button"
                                                    className="proof-file-remove"
                                                    onClick={() => handleRemoveFile(index)}
                                                    title="Remove"
                                                >
                                                    <HiOutlineTrash size={16} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div
                                    className="photo-upload-placeholder"
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    <HiOutlineCamera size={40} />
                                    <p>{files.length > 0 ? 'Add more files' : 'Click to upload photo(s) or PDF(s)'}</p>
                                    <span>JPG, PNG, PDF (Max 50MB each)</span>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,application/pdf"
                                    multiple
                                    onChange={handleFilesChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Delivery Note (Optional)</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Add delivery notes (e.g., Delivered to customer at door, Package handed to security, etc.)"
                                rows="4"
                            />
                        </div>

                        <div className="form-group form-group-check">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={invoicePending}
                                    onChange={(e) => setInvoicePending(e.target.checked)}
                                />
                                <span>🧾 Invoice not raised yet</span>
                            </label>
                            <small className="form-hint">
                                A separate flag, not a delivery status — the order can still be marked Delivered.
                            </small>
                        </div>

                        <div className="payment-section">
                            <h3>Payment Information</h3>
                            
                            <div className="form-group">
                                <label>Payment Type *</label>
                                <select
                                    value={paymentType}
                                    onChange={(e) => setPaymentType(e.target.value)}
                                    required
                                >
                                    <option value="COD">Cash on Delivery (COD)</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Card">Card</option>
                                    <option value="Online">Online Transfer</option>
                                    <option value="Credit">Credit</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Amount Paid (QAR)</label>
                                <input
                                    type="number"
                                    value={amountPaid}
                                    onChange={(e) => setAmountPaid(e.target.value)}
                                    placeholder="Enter amount collected from customer"
                                    min="0"
                                    max={order.amount}
                                    step="0.01"
                                />
                                <small className="form-hint">
                                    Total: QAR {order.amount?.toLocaleString()} | 
                                    Balance: QAR {((order.amount || 0) - (parseFloat(amountPaid) || 0)).toLocaleString()}
                                </small>
                            </div>

                            <div className="form-group">
                                <label>Payment Notes (Optional)</label>
                                <textarea
                                    value={paymentNotes}
                                    onChange={(e) => setPaymentNotes(e.target.value)}
                                    placeholder="Add payment notes (e.g., Partial payment, Will pay balance later, etc.)"
                                    rows="2"
                                />
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={uploading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={uploading || files.length === 0}
                            >
                                {uploading ? (
                                    <>
                                        <span className="spinner-small"></span> Uploading...
                                    </>
                                ) : (
                                    <>
                                        <HiOutlineUpload /> Upload Proof
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProofUploadModal;
